/** 统计筛选「节点」：动作发生时间维（与订单阶段分离）
 * 查询口径（同一日期窗口）：下单 createdAt · 发货 deliveredAt · 验收 acceptedAt
 * · 收款 预付/发货/验收/AR 的 paidAt · 超期 未付发货/验收应收 expire_time（已到期）
 * · 核验 finalCostConfirmedAt · 完成 completedAt（含历史履约回退）
 */

export const STATISTICS_PROCESS_NODE_OPTIONS = [
	{ value: 'created', label: '下单' },
	{ value: 'shipped', label: '发货' },
	{ value: 'accepted', label: '验收' },
	{ value: 'paid', label: '收款' },
	{ value: 'overdue', label: '超期' },
	{ value: 'verified', label: '核验' },
	{ value: 'completed', label: '完成' },
];

export const STATISTICS_PROCESS_NODE_VALUES = new Set(STATISTICS_PROCESS_NODE_OPTIONS.map((item) => item.value));

export const STATISTICS_PROCESS_NODE_MAX = 4;

export const getStatisticsProcessNodeLabel = (value) =>
	STATISTICS_PROCESS_NODE_OPTIONS.find((item) => item.value === value)?.label ?? String(value);

const sanitizeProcessNodes = (nodes = []) =>
	[...new Set((Array.isArray(nodes) ? nodes : []).filter((node) => STATISTICS_PROCESS_NODE_VALUES.has(node)))];

/** 显式节点链（不含由完成日期反推的展示节点） */
export const resolveExplicitStatisticsProcessNodes = (filters = {}) => sanitizeProcessNodes(filters.processNodes);

/** 默认仅「下单」时不向 API 传 processNodes，走旧 createdAt 路径 */
export const shouldSendStatisticsProcessNodesQuery = (filters = {}) => {
	const nodes = resolveExplicitStatisticsProcessNodes(filters);
	if (nodes.length === 0) return false;
	return !(nodes.length === 1 && nodes[0] === 'created');
};

const normalizeRelation = (value) => (value === 'or' ? 'or' : 'and');

/**
 * 从现有日期筛选推导节点链展示。
 * - 默认至少含「下单」
 * - 有完成日期时带上「完成」
 * - filters.processNodes 优先
 */
export const resolveStatisticsProcessNodes = (filters = {}) => {
	const raw = sanitizeProcessNodes(filters.processNodes);
	if (raw.length) return raw;
	const nodes = ['created'];
	if (filters.completedStartDate || filters.completedEndDate) nodes.push('completed');
	return nodes;
};

/** 相邻节点之间的关系，长度 = nodes.length - 1，形如「下单 与 发货 或 超期」 */
export const resolveStatisticsProcessNodeRelations = (filters = {}, nodes = resolveStatisticsProcessNodes(filters)) => {
	const edgeCount = Math.max(0, nodes.length - 1);
	const raw = Array.isArray(filters.processNodeRelations) ? filters.processNodeRelations : null;
	if (raw?.length) {
		return Array.from({ length: edgeCount }, (_, index) => normalizeRelation(raw[index] ?? 'and'));
	}
	// 兼容旧数据：仅有全局 dateRelation
	const fallback = normalizeRelation(filters.dateRelation);
	return Array.from({ length: edgeCount }, () => fallback);
};

/** 摘要文案：下单与发货或超期 */
export const formatStatisticsProcessNodeChainLabel = (filters = {}) => {
	const nodes = resolveStatisticsProcessNodes(filters);
	const relations = resolveStatisticsProcessNodeRelations(filters, nodes);
	if (!nodes.length) return '';
	return nodes
		.map((node, index) => {
			const label = getStatisticsProcessNodeLabel(node);
			if (index === 0) return label;
			const rel = relations[index - 1] === 'or' ? '或' : '与';
			return `${rel}${label}`;
		})
		.join('');
};

/** 下单/完成之间的关系 → 现有 dateRelation 查询字段 */
const deriveDateRelationFromChain = (nodes, relations) => {
	const createdIndex = nodes.indexOf('created');
	const completedIndex = nodes.indexOf('completed');
	if (createdIndex < 0 || completedIndex < 0) return 'and';
	const from = Math.min(createdIndex, completedIndex);
	const to = Math.max(createdIndex, completedIndex);
	for (let index = from; index < to; index += 1) {
		if (relations[index] === 'or') return 'or';
	}
	return 'and';
};

const alignRelationsToNodes = (nodes, relations, { appendRelation = 'and' } = {}) => {
	const edgeCount = Math.max(0, nodes.length - 1);
	const next = [];
	for (let index = 0; index < edgeCount; index += 1) {
		next.push(normalizeRelation(relations?.[index] ?? appendRelation));
	}
	return next;
};

/**
 * 节点变更 → 同步完成日期 / 逐段关系 / dateRelation（不改下单区间本身）
 * @param {string[]} nextNodes
 * @param {('and'|'or')[]} nextRelations 长度应为 nextNodes.length - 1
 */
export const buildProcessNodesFilterPatch = (filters = {}, nextNodes, nextRelations) => {
	const nodes = [...new Set((nextNodes ?? []).filter(Boolean))];
	const ensured = nodes.length ? nodes : ['created'];
	const relations = alignRelationsToNodes(ensured, nextRelations);
	const dateRelation = deriveDateRelationFromChain(ensured, relations);
	const hasCompleted = ensured.includes('completed');
	const rangeStart = filters.startDate ?? null;
	const rangeEnd = filters.endDate ?? null;

	return {
		processNodes: ensured,
		processNodeRelations: relations,
		dateRelation,
		completedStartDate: hasCompleted ? rangeStart : null,
		completedEndDate: hasCompleted ? rangeEnd : null,
		completedDateAnchor: hasCompleted ? filters.completedDateAnchor ?? filters.trendAnchor ?? null : null,
	};
};

/** 删除节点后重算关系：中间删除保留左侧关系接到右侧 */
export const removeProcessNodeAt = (nodes, relations, index) => {
	if (nodes.length <= 1 || index < 0 || index >= nodes.length) {
		return { nodes, relations };
	}
	const nextNodes = nodes.filter((_, i) => i !== index);
	let nextRelations = [...relations];
	if (index === 0) {
		nextRelations = relations.slice(1);
	} else if (index === nodes.length - 1) {
		nextRelations = relations.slice(0, -1);
	} else {
		nextRelations = [...relations.slice(0, index - 1), relations[index - 1], ...relations.slice(index + 1)];
	}
	return { nodes: nextNodes, relations: alignRelationsToNodes(nextNodes, nextRelations) };
};

/** 区间变更时：若选了「完成」，保持完成日与主区间一致 */
export const buildProcessNodesDateRangeSidePatch = (filters = {}, rangePatch = {}) => {
	const nodes = resolveStatisticsProcessNodes({ ...filters, ...rangePatch });
	if (!nodes.includes('completed')) return {};
	const startDate = 'startDate' in rangePatch ? rangePatch.startDate : filters.startDate;
	const endDate = 'endDate' in rangePatch ? rangePatch.endDate : filters.endDate;
	return {
		completedStartDate: startDate ?? null,
		completedEndDate: endDate ?? null,
	};
};
